import type { ReactNode } from 'react';
import { cn, TEAM_COLOR, upper, type Side } from './util';

export interface TeamNameProps {
  team: Side;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Show the coloured letter tile (A / B / J) before the name — colour is never the only cue. */
  showLetter?: boolean;
  /** Render the name in the team colour with an ink outline (true) or plain ink (false). */
  colored?: boolean;
  suffix?: ReactNode;
  /** On phones, wrap a long name onto more lines instead of truncating it (narrow columns). */
  wrapOnPhone?: boolean;
  className?: string;
}

const SIZE = {
  sm: { text: 'text-[16px]', tile: 'size-5 text-[13px] border-2' },
  md: { text: 'text-[22px]', tile: 'size-7 text-[17px] border-2' },
  lg: { text: 'text-[32px]', tile: 'size-9 text-[22px] border-3' },
  xl: { text: 'text-[52px]', tile: 'size-13 text-[32px] border-3' },
} as const;

/** Team name in Bangers with its colour and A/B letter tile. Uppercased with Turkish rules. */
export function TeamName({
  team,
  name,
  size = 'md',
  showLetter = true,
  colored = true,
  suffix,
  wrapOnPhone,
  className,
}: TeamNameProps) {
  const s = SIZE[size];
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      {showLetter && (
        <span
          aria-label={team === 'J' ? 'Jüri' : `Takım ${team}`}
          className={cn(
            'comic flex shrink-0 items-center justify-center border-ink pt-[0.1em] text-white shadow-ink-xs',
            s.tile,
          )}
          style={{ backgroundColor: TEAM_COLOR[team] }}
        >
          <span className="ink-text">{team}</span>
        </span>
      )}
      <span
        className={cn(
          'comic min-w-0 truncate pt-[0.08em]',
          s.text,
          colored && (size === 'sm' ? '' : 'ink-text'),
          wrapOnPhone && 'max-sm:whitespace-normal',
        )}
        style={colored ? { color: TEAM_COLOR[team] } : undefined}
      >
        {upper(name)}
      </span>
      {suffix}
    </span>
  );
}
